import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'ds-switch',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      class="ds-switch"
      type="button"
      role="switch"
      [class.ds-switch--checked]="checked"
      [disabled]="disabled"
      [attr.aria-checked]="checked"
      [attr.aria-label]="ariaLabel"
      (click)="toggle()"
      (focus)="focused.emit($event)"
      (blur)="blurred.emit($event)"
    >
      <span class="ds-switch__track" aria-hidden="true">
        <span class="ds-switch__thumb"></span>
      </span>
    </button>
  `,
  styles: [`
    :host {
      display: inline-flex;
      line-height: 0;
    }

    .ds-switch {
      width: var(--ds-switch-container-width);
      height: var(--ds-switch-container-height);
      display: inline-grid;
      place-items: center;
      padding: 0;
      border: 0 solid transparent;
      border-radius: var(--ds-switch-container-radius);
      background: transparent;
      box-sizing: border-box;
      cursor: pointer;
      transition:
        background-color var(--ds-switch-transition-duration) ease,
        border-color var(--ds-switch-transition-duration) ease,
        border-width var(--ds-switch-transition-duration) ease,
        opacity var(--ds-switch-transition-duration) ease;
    }

    .ds-switch:hover:not(:disabled) {
      background: var(--ds-switch-hover-background);
    }

    .ds-switch:focus-visible {
      border-width: var(--ds-switch-focus-border-width);
      border-color: var(--ds-switch-focus-border-color);
      outline: none;
    }

    .ds-switch:disabled {
      opacity: calc(var(--ds-switch-disabled-opacity) / 100px);
      cursor: not-allowed;
    }

    .ds-switch__track {
      width: var(--ds-switch-track-width);
      height: var(--ds-switch-track-height);
      position: relative;
      display: inline-block;
      border-radius: var(--ds-switch-track-radius);
      background: var(--ds-switch-track-background);
      box-sizing: border-box;
      transition: background-color var(--ds-switch-transition-duration) ease;
    }

    .ds-switch--checked .ds-switch__track {
      background: var(--ds-switch-track-selected-background);
    }

    .ds-switch__thumb {
      width: var(--ds-switch-thumb-size);
      height: var(--ds-switch-thumb-size);
      position: absolute;
      top: 50%;
      left: var(--ds-switch-thumb-inset);
      border-radius: 50%;
      background: var(--ds-switch-thumb-background);
      transform: translate(0, -50%);
      transition: transform var(--ds-switch-transition-duration) ease;
    }

    .ds-switch--checked .ds-switch__thumb {
      transform: translate(
        calc(var(--ds-switch-track-width) - var(--ds-switch-thumb-size) - var(--ds-switch-thumb-inset) - var(--ds-switch-thumb-inset)),
        -50%
      );
    }
  `],
})
export class DsSwitchComponent {
  @Input() checked = false;
  @Input() disabled = false;
  @Input() ariaLabel = 'Switch';

  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() focused = new EventEmitter<FocusEvent>();
  @Output() blurred = new EventEmitter<FocusEvent>();

  toggle(): void {
    if (this.disabled) return;
    this.checked = !this.checked;
    this.checkedChange.emit(this.checked);
  }
}
